import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Beaker, FlaskConical, Plus, Upload } from "lucide-react";
import axios from "axios";

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  return (
    <div className="min-h-screen bg-noir flex">
      <Sidebar />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-playfair text-3xl font-bold text-white mb-2">
              Welcome back, {user?.name?.split(' ')[0] || 'Perfumer'}
            </h1>
            <p className="text-zinc-400">Your perfumery workspace at a glance</p>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <StatCard 
              icon={<Beaker className="w-5 h-5" />}
              title="Materials"
              value={loading ? "..." : stats?.materials_count || 0}
              link="/inventory"
            />
            <StatCard 
              icon={<FlaskConical className="w-5 h-5" />}
              title="Recipes"
              value={loading ? "..." : stats?.recipes_count || 0}
              link="/my-recipes"
            />
          </div>

          {/* Quick Actions */}
          <Card className="bg-noir-paper border-zinc-800 mb-8">
            <CardHeader>
              <CardTitle className="text-white font-playfair">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-3">
              <Link to="/inventory">
                <Button 
                  data-testid="add-material-btn"
                  variant="outline" 
                  className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-800/50 hover:text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Material
                </Button>
              </Link>
              <Link to="/recipe-builder">
                <Button 
                  data-testid="create-recipe-btn"
                  variant="outline" 
                  className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-800/50 hover:text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Recipe
                </Button>
              </Link>
              <Link to="/import">
                <Button 
                  data-testid="import-recipe-btn"
                  variant="outline" 
                  className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-800/50 hover:text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import from File
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Items */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Materials */}
            <Card className="bg-noir-paper border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white font-playfair">Recent Materials</CardTitle>
                <Link to="/inventory" className="text-amber text-sm hover:text-amber-light">
                  View All
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-zinc-500">Loading...</div>
                ) : stats?.recent_materials?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_materials.map((material) => (
                      <div 
                        key={material.material_id}
                        className="flex items-center justify-between p-3 bg-noir-subtle border border-zinc-800 hover:border-zinc-700 transition-colors"
                      >
                        <div>
                          <p className="text-white font-medium">{material.name}</p>
                          <p className="text-zinc-500 text-sm font-mono">
                            {material.cas_number || "No CAS"}
                          </p>
                        </div>
                        <span className="text-amber text-sm font-mono">
                          {material.solution_percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Beaker className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500">No materials yet</p>
                    <Link to="/inventory" className="text-amber text-sm hover:text-amber-light">
                      Add your first material
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Recipes */}
            <Card className="bg-noir-paper border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white font-playfair">Recent Recipes</CardTitle>
                <Link to="/my-recipes" className="text-amber text-sm hover:text-amber-light">
                  View All
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-zinc-500">Loading...</div>
                ) : stats?.recent_recipes?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_recipes.map((recipe) => (
                      <Link 
                        key={recipe.recipe_id}
                        to={`/recipe-builder/${recipe.recipe_id}`}
                        className="block p-3 bg-noir-subtle border border-zinc-800 hover:border-amber/30 transition-colors"
                      >
                        <p className="text-white font-medium">{recipe.name}</p>
                        <p className="text-zinc-500 text-sm">
                          {recipe.ingredients?.length || 0} ingredients • {recipe.total_grams}g
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FlaskConical className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500">No recipes yet</p>
                    <Link to="/recipe-builder" className="text-amber text-sm hover:text-amber-light">
                      Create your first recipe
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ icon, title, value, link }) => (
  <Link to={link}>
    <Card className="bg-noir-paper border-zinc-800 hover:border-amber/30 transition-colors cursor-pointer">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-zinc-400 text-sm mb-1">{title}</p>
            <p className="text-3xl font-bold text-white font-mono">{value}</p>
          </div>
          <div className="w-12 h-12 bg-amber/10 flex items-center justify-center text-amber">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);
